import React, { useEffect, useState } from 'react';
import { getCachedPhoto } from '../cache/UserPhoto';

export const generateCircleImage = async (images: string[], maxLength: number): Promise<string | null> => {
  if (!images.length || maxLength < 1) return null;

  const size = 32;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  canvas.width = size;
  canvas.height = size;
  ctx.clearRect(0, 0, size, size);

  const loadImage = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.src = src;
    });

  const clippedRegions = (count: number) => {
    if (count === 1) {
      return [{ x: 0, y: 0, w: size, h: size }];
    } else if (count === 2) {
      return [
        { x: 0, y: 0, w: size / 2, h: size },
        { x: size / 2, y: 0, w: size / 2, h: size }
      ];
    } else {
      return [
        { x: 0, y: 0, w: size / 2, h: size },
        { x: size / 2, y: 0, w: size / 2, h: size / 2 },
        { x: size / 2, y: size / 2, w: size / 2, h: size / 2 }
      ];
    }
  };

  const drawSeparators = (ctx: CanvasRenderingContext2D, count: number) => {
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 1;

    if (count >= 2) {
      ctx.beginPath();
      ctx.moveTo(size / 2, 0);
      ctx.lineTo(size / 2, size);
      ctx.stroke();
    }

    if (count === 3) {
      ctx.beginPath();
      ctx.moveTo(size / 2, size / 2);
      ctx.lineTo(size, size / 2);
      ctx.stroke();
    }
  };

  const loadedImages = await Promise.all(images.slice(0, maxLength).map(loadImage));
  const count = loadedImages.length;
  const layoutRegions = clippedRegions(count);

  ctx.save();
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
  ctx.clip();

  // this is there the magic around the images in the circle happens
  loadedImages.forEach((img, i) => {
    const region = layoutRegions[i];
    if (!region) return;

    const { x, y, w, h } = region;

    // scale to cover the region fully (like CSS background-size: cover)
    const scale = Math.max(w / img.width, h / img.height);
    const dw = img.width * scale;
    const dh = img.height * scale;
    const dx = x + (w - dw) / 2;
    const dy = y + (h - dh) / 2;

    ctx.save();

    // outer circle clip
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
    ctx.clip();

    // region rectangle clip - so image doesn’t bleed past separator
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();

    ctx.drawImage(img, dx, dy, dw, dh);

    ctx.restore();
  });

  drawSeparators(ctx, count);
  ctx.restore();

  return canvas.toDataURL('image/png');
};

export const AsyncCircleImage: React.FC<{ members: any[] }> = ({ members }) => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!members || members.length === 0) {
      return;
    }

    const load = async () => {
      const images: (string | null)[] = [];
      for (const user of members) {
        const id = user?.AadObjectId ?? user.UserName;
        const imageUrl = await getCachedPhoto(id);
        if (imageUrl && imageUrl.length > 0 )
          images.push(imageUrl);
      }

      const image = await generateCircleImage(images, 3);
      setSrc(image);
    };

    load();
  }, [members]);

  return src ? (
    <img
      src={src}
      style={{ borderRadius: '50%', width: 24, height: 24, marginRight: 4 }}
    />
  ) : (
    <span />
  );
};
